import React, { useEffect, useState } from 'react';
import { Modal } from 'react-native';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../redux/store';
import { startStandaloneSession, abandonSession } from '../../redux/cookingSessionSlice';
import { recordCooked } from '../../redux/recipeMetadataSlice';
import { Recipe } from '../../types/recipe';
import { CookingStepScreen } from './CookingStepScreen';
import { CookingDoneScreen } from './CookingDoneScreen';

interface Props {
  recipe: Recipe;
  visible: boolean;
  onClose: () => void;
}

/**
 * Standalone cooking path (opened from the recipe detail page). All step UI
 * lives in the shared CookingStepScreen; this wrapper owns the modal shell,
 * the standalone session lifecycle, and the done screen.
 */
const CookingModeModal: React.FC<Props> = ({ recipe, visible, onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [showDone, setShowDone] = useState(false);

  // Start a fresh session when the modal opens
  useEffect(() => {
    if (visible && recipe.id) {
      setShowDone(false);
      dispatch(startStandaloneSession({ recipeId: String(recipe.id) }));
    }
  }, [visible, recipe.id]);

  const handleClose = () => {
    dispatch(abandonSession());
    onClose();
  };

  const handleFinish = () => {
    dispatch(recordCooked(String(recipe.id)));
    setShowDone(true);
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="fullScreen"
      onRequestClose={handleClose}
    >
      {showDone ? (
        <CookingDoneScreen
          recipeId={String(recipe.id)}
          recipeTitle={recipe.title}
          onClose={onClose}
        />
      ) : (
        <CookingStepScreen recipe={recipe} onQuit={handleClose} onFinish={handleFinish} />
      )}
    </Modal>
  );
};

export default CookingModeModal;
